import { observer } from "mobx-react-lite";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import Dropzone from "react-dropzone";
import { FiUploadCloud, FiX } from "react-icons/fi";
import { FaSpinner } from "react-icons/fa";
import { GrDocument, GrDocumentPdf, GrImage } from "react-icons/gr";
import { Button, Modal, ModalBody } from "reactstrap";
import { formatBytes } from "../index";
import { DialogContext } from "../../store/context/DialogContext";
import { useTranslation } from "../../store/context/TranslationContext";
import { doDELETE } from "../../util/HttpUtil";
import { isDoc, isImage, isPdf, isPpt } from "../DocUpload/DocViewer";
import { isLocalFile, isStoredAttachment, storedAttachmentIndex } from "../DocUpload/attachmentUtils";

const FILE_EXTENSIONS = {
  image: [".png", ".jpeg", ".jpg"],
  pdf: [".pdf"],
  doc: [".doc", ".docx", ".txt"],
  ppt: [".ppt", ".pptx"],
};

const getDropzoneAccept = (supportedFiles) => {
  const accept = {};
  if (supportedFiles.includes("image")) accept["image/*"] = FILE_EXTENSIONS.image;
  if (supportedFiles.includes("pdf")) accept["application/pdf"] = FILE_EXTENSIONS.pdf;
  if (supportedFiles.includes("doc")) {
    accept["application/msword"] = [".doc"];
    accept["application/vnd.openxmlformats-officedocument.wordprocessingml.document"] = [".docx"];
    accept["text/plain"] = [".txt"];
  }
  if (supportedFiles.includes("ppt")) {
    accept["application/vnd.ms-powerpoint"] = [".ppt"];
    accept["application/vnd.openxmlformats-officedocument.presentationml.presentation"] = [".pptx"];
  }
  return accept;
};

const getSupportedFilesString = (supportedFiles) =>
  supportedFiles
    .flatMap((type) => FILE_EXTENSIONS[type] || [])
    .map((e) => e.replace(".", "").toUpperCase())
    .join(", ");

const getFilenameFromPath = (path) => {
  const parts = path?.split("/") ?? [];
  return parts[parts.length - 1] ?? "";
};

const truncateFilename = (filename = "") => {
  if (filename.length > 28) return `${filename.slice(0, 12)}…${filename.slice(-10)}`;
  return filename;
};

const getFilenameFromContentDisposition = (headerValue) => {
  if (!headerValue) return null;
  const match = headerValue.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
  return match?.[1] ? decodeURIComponent(match[1].replace(/"/g, "")) : null;
};

const getPreviewKind = (file) => {
  if (isPdf(file)) return "pdf";
  if (isImage(file)) return "image";
  if (isDoc(file) || isPpt(file)) return "office";
  return "office";
};

const isApiUrl = (src) => typeof src === "string" && src.startsWith("/api");

const PreviewThumb = ({ file }) => {
  const [modal, setModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [downloadName, setDownloadName] = useState(file?.name || getFilenameFromPath(file?.key ?? file));
  const previewObjectUrlRef = useRef(null);
  const { showError } = useContext(DialogContext);
  const { t } = useTranslation();

  const fileSrc = file?.key ?? file;
  const previewKind = getPreviewKind(file);
  const isLocalPreviewableImage = previewKind === "image" && typeof fileSrc === "string" && !isApiUrl(fileSrc);

  useEffect(() => {
    return () => {
      if (previewObjectUrlRef.current) {
        window.URL.revokeObjectURL(previewObjectUrlRef.current);
      }
    };
  }, []);

  const loadProtectedPreview = async () => {
    if (!isApiUrl(fileSrc)) {
      setPreviewUrl(typeof fileSrc === "string" ? fileSrc : "");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("BearerToken");
      const response = await fetch(fileSrc, {
        headers: token ? { Authorization: token } : {},
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`Failed to load file: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      if (previewObjectUrlRef.current) {
        window.URL.revokeObjectURL(previewObjectUrlRef.current);
      }
      previewObjectUrlRef.current = blobUrl;
      setPreviewUrl(blobUrl);
      setDownloadName(
        getFilenameFromContentDisposition(response.headers.get("content-disposition")) ||
          file?.name ||
          getFilenameFromPath(fileSrc)
      );
    } catch (error) {
      showError(error?.message || "Failed to load file preview");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPreview = async () => {
    setModal(true);
    if (!previewUrl) {
      await loadProtectedPreview();
    }
  };

  const thumbContent = useMemo(() => {
    if (loading) return <FaSpinner className="docv2__thumb-spinner" size={16} />;
    if (isLocalPreviewableImage) {
      return <img src={fileSrc} alt={downloadName || "Preview"} className="docv2__file-thumb-image" />;
    }
    if (previewKind === "pdf") return <GrDocumentPdf size={22} />;
    if (previewKind === "image") return <GrImage size={22} />;
    return <GrDocument size={22} />;
  }, [downloadName, fileSrc, isLocalPreviewableImage, loading, previewKind]);

  return (
    <>
      <button
        type="button"
        className="docv2__file-thumb-button"
        onClick={handleOpenPreview}
        aria-label={t("Preview file")}
      >
        {thumbContent}
      </button>

      <Modal size={previewKind === "office" ? undefined : "xl"} isOpen={modal} toggle={() => setModal(false)} centered>
        <ModalBody className="docv2__preview-modal">
          {loading ? (
            <div className="docv2__preview-loading">
              <FaSpinner className="docv2__thumb-spinner" size={18} />
              <span>{t("Loading preview...")}</span>
            </div>
          ) : previewKind === "image" && previewUrl ? (
            <img src={previewUrl} alt={downloadName || "Preview"} className="docv2__preview-image" />
          ) : previewKind === "pdf" && previewUrl ? (
            <object
              data={previewUrl}
              type="application/pdf"
              className="docv2__preview-frame"
            >
              <p>
                {t("Your browser does not support PDFs.")}{" "}
                <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                  {t("Download the PDF")}
                </a>
              </p>
            </object>
          ) : previewKind === "office" ? (
            <div className="docv2__preview-download">
              <GrDocument size={28} />
              <h5>{t("Preview not available")}</h5>
              <p>{t("You can download and open this file locally.")}</p>
              {previewUrl ? (
                <Button color="primary" tag="a" href={previewUrl} download={downloadName}>
                  {t("Download")}
                </Button>
              ) : (
                <Button color="primary" disabled>
                  {t("Preparing download...")}
                </Button>
              )}
            </div>
          ) : (
            <div className="docv2__preview-download">
              <p>{t("Preview not available.")}</p>
            </div>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

const FileChip = ({ file, onRemove, asLabel }) => {
  const displayName = truncateFilename(file?.name ?? getFilenameFromPath(file?.key ?? file));
  return (
    <div className="docv2__file">
      <div className="docv2__file-thumb">
        <PreviewThumb file={file} />
      </div>
      <div className="docv2__file-meta">
        <div className="docv2__file-name" title={file?.name ?? displayName}>{displayName}</div>
        {file?.formattedSize ? <div className="docv2__file-size">{file.formattedSize}</div> : null}
      </div>
      {!asLabel && (
        <button type="button" className="docv2__file-remove" aria-label="Remove file" onClick={onRemove}>
          <FiX size={14} />
        </button>
      )}
    </div>
  );
};

const DropzoneV2Compact = ({
  asLabel,
  value,
  onChange = () => {},
  onDeleteAttachment = () => {},
  onDelete,
  module,
  field,
  id,
  supportedFiles = ["image", "pdf"],
  single = false,
  fileSizeText,
}) => {
  const [selectedFiles, setSelectedFiles] = useState(value ?? []);
  const { showConfirm, showMessage, showError } = useContext(DialogContext);
  const { t } = useTranslation();

  useEffect(() => {
    setSelectedFiles(value ?? []);
  }, [value]);

  const handleAcceptedFiles = (files) => {
    const maxFileSize = 104857600; // 100 MB
    const supportedFileExtensions = supportedFiles.flatMap((type) => FILE_EXTENSIONS[type] || []);

    const invalidFiles = files.filter((file) => {
      if (!file) return true;
      const ext = file?.name?.slice(file?.name?.lastIndexOf("."))?.toLowerCase();
      return !supportedFileExtensions.includes(ext) || file.size > maxFileSize;
    });

    if (invalidFiles.length > 0 || !files?.length) {
      const exts = getSupportedFilesString(supportedFiles);
      showError(`Unsupported file format or file too large. Please upload only ${exts} files (max 100 MB).`);
      return;
    }

    files.forEach((file) =>
      Object.assign(file, {
        key: URL.createObjectURL(file),
        formattedSize: formatBytes(file.size),
      })
    );

    if (single) {
      setSelectedFiles([files[0]]);
      onChange([files[0]]);
    } else {
      const next = [...selectedFiles, ...files];
      setSelectedFiles(next);
      onChange(next);
    }
  };

  const handleLocalRemove = async (file) => {
    if (isLocalFile(file)) {
      const next = selectedFiles.filter((f) => f !== file);
      setSelectedFiles(next);
      onChange(next);
      return;
    }
    if (typeof file === "string") {
      if (
        await showConfirm({
          title: t("Do you want to delete file?"),
          description: t("This is an unrecoverable operation."),
        })
      ) {
        try {
          const response = await doDELETE("/api/file/delete/single", { _id: id, field, module });
          if (response.status === 200) {
            showMessage("Deleted", "Deleted");
            onDelete?.();
          }
        } catch (e) {
          showError(e);
        }
      }
      return;
    }
    if (isStoredAttachment(file)) {
      const deleted = await onDeleteAttachment(storedAttachmentIndex(selectedFiles, file));
      if (deleted) setSelectedFiles((prev) => prev.filter((f) => f !== file));
    }
  };

  const renderedFiles = (selectedFiles ?? []).filter((f) => f !== undefined);
  const hideDropzone = asLabel || (single && renderedFiles.length > 0);

  return (
    <div>
      {renderedFiles.length > 0 && (
        <div className="docv2__file-list">
          {renderedFiles.map((f, i) => (
            <FileChip
              key={`${f?.key || f?.name || "file"}-${i}`}
              file={f}
              asLabel={asLabel}
              onRemove={() => handleLocalRemove(f)}
            />
          ))}
        </div>
      )}

      {!hideDropzone && (
        <Dropzone onDrop={handleAcceptedFiles} accept={getDropzoneAccept(supportedFiles)}>
          {({ getRootProps, getInputProps, isDragActive }) => (
            <div
              {...getRootProps()}
              className={`docv2__dropzone ${isDragActive ? "docv2__dropzone--active" : ""}`}
            >
              <input {...getInputProps()} />
              <FiUploadCloud className="docv2__dropzone-icon" />
              <div>
                <div className="docv2__dropzone-text">
                  <strong>{t("Click to upload")}</strong> {t("or drag & drop")}
                </div>
                <div className="docv2__dropzone-meta">
                  {getSupportedFilesString(supportedFiles)} · {t("Max")} {fileSizeText || "100 MB"}
                </div>
              </div>
            </div>
          )}
        </Dropzone>
      )}
    </div>
  );
};

export default observer(DropzoneV2Compact);
